import { defineComponent as _defineComponent } from 'vue'
import { fetchTokenTrades } from "@/api/endpoints";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import Spinner from "../common/Spinner.vue";
import TableResponsiveIndexed from "../common/TableResponsiveIndexed.vue";
import { formatPrice } from "@/shared";
import SignatureAddress from "../common/SignatureAddress.vue";
import AccountAddress from "../common/AccountAddress.vue";
import AmountEmoji from "../common/AmountEmoji.vue";
import PlatformImage from "../common/PlatformImage.vue";
import { useAlertStore } from "@/pinia";
import TokenImage from "../common/TokenImage.vue";
import { PublicKey } from "@solana/web3.js";
import router from "@/router";
import TimeAgo from "../common/TimeAgo.vue";

// computed

export default /*#__PURE__*/_defineComponent({
  __name: 'CandleInspector',
  setup(__props) {

const datesAreValid = computed<boolean>(() => {
	return end.value > start.value;
});
const mintIsInvalid = computed<boolean>(() => {
	try {
		if (!mint.value) return false;
		return !PublicKey.isOnCurve(new PublicKey(mint.value).toString());
	} catch {
		return true;
	}
});
const getIDs = computed<string[]>(() => {
	const ids = [];
	for (const i in trades.value) {
		const id = getUniqueIDFromTrade(trades.value[i]);
		ids.push(id);
		trades.value[i].id = id;
	}

	return ids;
});
const getTrade = computed<(id: string) => any>(() => (id) => {
	const tx = trades.value.find((trade) => trade.id === id);

	return {
		id: tx.id,
		signature: tx.signature,
		amount: Number(tx.trade.inAmount),
		inMint: tx.trade.inMint,
		outMint: tx.trade.outMint,
		platform: tx.platform ? tx.platform : tx.trade.program,
		price: tx.trade.inMint === mint.value ? tx.inPrice : tx.outPrice,
		program: tx.trade.program,
		sell: tx.trade.inMint === mint.value,
		signer: tx.trade.signer,
		timestamp: tx.trade.createdAt
			? new Date(tx.trade.createdAt * 1000).getTime()
			: Date.now(),
		value: tx.trade.value,
		inAmount: tx.trade.inAmount,
		outAmount: tx.trade.outAmount,
		createdAt: tx.trade.createdAt,
	};
});

// store
const alertStore = useAlertStore();

// data
const inspectorChannel = new BroadcastChannel("candle:inspector");
const refModal = ref<HTMLDialogElement>();
const trades = ref<any[]>([]);
const loading = ref<boolean>(false);
const wallet = ref<string>("");
const mint = ref<string>("");
const startDate = ref<string>("");
const start = ref<number>(0);
const end = ref<number>(0);
const endDate = ref<string>("");
const limit = ref<number>(10);
const limits = ref<number[]>([10, 25, 50, 100]);
let controller = new AbortController();

// methods
function show() {
	refModal.value?.show();
	document.body.classList.add("overflow-hidden");
}
function hide() {
	refModal.value?.close();
	document.body.classList.remove("overflow-hidden");
}
function resetController() {
	controller = new AbortController();
	controller.signal.throwIfAborted();
	controller.signal.addEventListener("abort", () => {
		loading.value = false;
	});
}
function mounted() {
	router.afterEach(()=>{
		refModal.value?.close()
	})

	inspectorChannel.onmessage = ({ data }) => {
		if (!data.start || !data.end || !data.mint || !data.wallet) return;
		show();
		start.value = data.start;
		end.value = data.end;
		wallet.value = data.wallet;
		mint.value = data.mint;
		startDate.value = formatTimestampForInput(data.start);
		endDate.value = formatTimestampForInput(data.end);

		search();
	};
}
function unmounted() {
	inspectorChannel.close();
}
function getUniqueIDFromTrade(trade: any): string {
	return `${trade.signature}:${trade.inPrice * trade.outPrice}:${
		(trade.trade.inAmount / trade.trade.outAmount) * trade.trade.value
	}`;
}
async function search() {
	// if (loading.value) return;
	if (mintIsInvalid.value) return;

	controller.abort();
	resetController();

	try {
		loading.value = true;
		const t = await fetchTokenTrades({
			from: start.value / 1000,
			to: end.value / 1000,
			mint: mint.value,
			wallet: wallet.value,
			limit: limit.value,
			controller: controller.signal,
		});

		if (t.error) {
			throw new Error(t.error);
		}

		trades.value = t;
	} catch (e: any) {
		alertStore.error(e.message);
	} finally {
		loading.value = false;
	}
}
function formatTimestampForInput(timestamp) {
	const date = new Date(timestamp); // Convert seconds to milliseconds
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0"); // Ensure 2 digits
	const day = String(date.getDate()).padStart(2, "0");
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");

	return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// setup
onMounted(mounted);
onUnmounted(unmounted);

// watch
watch(
	() => startDate.value,
	() => {
		start.value = new Date(startDate.value).getTime();
	}
);
watch(
	() => endDate.value,
	() => {
		end.value = new Date(endDate.value).getTime();
	}
);

return { __sfc: true,datesAreValid, mintIsInvalid, getIDs, getTrade, alertStore, inspectorChannel, refModal, trades, loading, wallet, mint, startDate, start, end, endDate, limit, limits, controller, show, hide, resetController, mounted, unmounted, getUniqueIDFromTrade, search, formatTimestampForInput, Spinner, TableResponsiveIndexed, formatPrice, SignatureAddress, AccountAddress, AmountEmoji, PlatformImage, TokenImage, TimeAgo }
}

})